import Client from "./_client"; 

export const getBatchInfo = async (batchId) => {
  let url = `analyticsV2/batch/${batchId}/info`;
  const result = await new Client({ path: url }).get();
  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get batch info");
  }
  return result?.data;
};

export const getLeaderboard = async ({ type, id, interviewVariantType, leaderboardType }) => {
  if (!['batch', 'interview'].includes(type)) {
    throw new Error('Invalid leaderboard type');
  }
  let url = `analyticsV2/${type}/${id}/leaderboard?interviewVariantType=${interviewVariantType}`;
  
  if (leaderboardType) {
    url += `&type=${leaderboardType}`;
  }
  
  const result = await new Client({ path: url }).get();
  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || `Uh Oh! Unable to get ${type} leaderboard`);
  }
  return result?.data;
};

export const getBatchUsage = async (batchId, interviewVariantType, duration) => {
  let url = `analyticsV2/batch/${batchId}/usage?interviewVariantType=${interviewVariantType}&duration=${duration}`;

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get batch usage");
  }
  return result?.data;
};

export const getBatchPerformanceOverview = async (batchId, interviewVariantType) => {
  let url = `analyticsV2/batch/${batchId}/performance-overview?interviewVariantType=${interviewVariantType}`;

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get batch performance overview");
  }
  return result?.data;
};

export const getBatchHighlights = async (batchId, interviewVariantType, highlightType, pageSize = 100, pageNumber = 1, duration = 'last_3_months', additionalParams = {}) => {
  let url = `analyticsV2/batch/${batchId}/highlights?interviewVariantType=${interviewVariantType}&highlightType=${highlightType}&pageSize=${pageSize}&pageNumber=${pageNumber}&duration=${duration}`;
  
  // Add additional parameters to URL
  Object.keys(additionalParams).forEach(key => {
    if (additionalParams[key] !== undefined && additionalParams[key] !== null && additionalParams[key] !== '') {
      url += `&${key}=${encodeURIComponent(additionalParams[key])}`;
    }
  });

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get batch highlights");
  }
  return result?.data;
};

export const getBatchRecentTemplateScores = async (batchId, interviewVariantType) => {
  let url = `analyticsV2/batch/${batchId}/recent-template-scores?interviewVariantType=${interviewVariantType}`;

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get batch recent template scores");
  }
  return result?.data;
}; 

export const getBatchUserAnalytics = async (filters) => {

  let url = `analyticsV2/batch/${filters.batchId}/user-analytics?interviewVariantType=${filters.interviewVariantType}`;

  // Add any additional filters as query parameters
  Object.keys(filters).forEach(key => {
    if (key !== 'batchId' && key !== 'interviewVariantType' && filters[key] !== undefined && filters[key] !== null) {
      url += `&${key}=${encodeURIComponent(filters[key])}`;
    }
  });

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get batch user analytics");
  }
  return result?.data;
}; 

export const getStudentScoreSummary = async (studentId, interviewVariantType, batchId) => {
  let url = `analyticsV2/student/${studentId}/score-summary?interviewVariantType=${interviewVariantType}`;
  if (batchId) {
    url += `&batch=${batchId}`;
  }

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get student score summary");
  }
  return result?.data;
}; 

export const getInterviewPerformance = async (interviewId, interviewVariantType, params = {}) => {
  let url = `analyticsV2/interview/${interviewId}/performance?`;

  // Append additional params (page, pageSize, filterByName, toExport, etc.)
  Object.keys(params || {}).forEach(key => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }
  });

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get interview performance");
  }
  return result?.data;
}; 


export const getStudentRecentInterviewScoreAndAttempts = async (filters) => {
  const { studentId, interviewVariantType } = filters;
  let url = `analyticsV2/student/${studentId}/recent-interview-score-and-attempts?interviewVariantType=${interviewVariantType}`;
  
  // Add any additional filters as query parameters
  Object.keys(filters).forEach(key => {
    if (key !== 'studentId' && key !== 'interviewVariantType' && filters[key] !== undefined && filters[key] !== null) {
      url += `&${key}=${encodeURIComponent(filters[key])}`;
    }
  });

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get recent interview score and attempts");
  }
  return result?.data;
}; 

export const getStudentAttemptRate = async (studentId, interviewVariantType, batchId) => {
  let url = `analyticsV2/student/${studentId}/attempt-rate?interviewVariantType=${interviewVariantType}`;
  if (batchId) {
    url += `&batch=${batchId}`;
  }

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get student attempt rate");
  }
  return result?.data;
}; 

export const getAverageAttemptRateDetails = async ({ type, id, interviewVariantType }) => {
  if (!['batch', 'interview'].includes(type)) {
    throw new Error('Invalid type for average attempt rate details');
  }
  let url = `analyticsV2/${type}/${id}/average-attempt-rate-details?interviewVariantType=${interviewVariantType}`;
  const result = await new Client({ path: url }).get();
  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || `Uh Oh! Unable to get ${type} average attempt rate details`);
  }
  return result?.data;
}; 

export const getStudentInterviewAttempts = async ({ studentId, interviewVariantType, interviewId, pageSize = 4, pageNumber = 1, orderBy = 'title', order = 'asc' }) => {
  let url = `analyticsV2/student/${studentId}/interview-attempts?interviewVariantType=${interviewVariantType}&interviewId=${interviewId}&pageSize=${pageSize}&pageNumber=${pageNumber}&orderBy=${orderBy}&order=${order}`;

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get student interview attempts");
  }
  return result?.data;
};

export const getInterviewDetails = async (interviewId, interviewVariantType) => {
  let url = `analyticsV2/interview/${interviewId}/details?interviewVariantType=${interviewVariantType}`;

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get interview details");
  }
  return result?.data;
};

export const getShortlistTaskAverageAndAttemptRate = async (taskId) => {
  let url = `analytics/shortlistTask/${taskId}/average-and-attempt-rate`;

  const result = await new Client({ path: url }).get();

  if (!(result.ok || result.code === 200)) {
    throw new Error(
      result.message || "Uh Oh! Unable to get shortlist task average and attempt rate");
  }
  return result?.data;
}; 